<script setup lang="ts">
import { dialogEvents } from "@/data-views/dialogs";

////////////////////////////////////////////////////////////
//////////////////// VARIABLES /////////////////////////////
////////////////////////////////////////////////////////////
const loading = ref(false);
const isVisible = ref(false);
const state = reactive({
  message: "",
  options: {} as {
    [key: string]: {
      label: string;
      color: string;
      handler: () => Promise<void>;
    };
  },
});

////////////////////////////////////////////////////////////
//////////////////// METHODS ///////////////////////////////
////////////////////////////////////////////////////////////
/**
 * Load the dialog
 */
async function load(
  message: string,
  options: {
    [key: string]: {
      label: string;
      color: string;
      handler: () => Promise<void>;
    };
  },
): Promise<void> {
  // Start loading
  loading.value = true;

  // Set the state
  state.message = message;
  state.options = options;

  // Stop loading
  loading.value = false;
}

async function handle(handler: () => Promise<void>): Promise<void> {
  // Start loading
  loading.value = true;

  // Execute the handler
  await handler();

  // Hide
  isVisible.value = false;

  // Stop loading
  loading.value = false;
}

////////////////////////////////////////////////////////////
//////////////////// LIFECYCLE /////////////////////////////
////////////////////////////////////////////////////////////
onMounted(() => {
  window.addEventListener(dialogEvents.OptionDialog.OPEN, ({ detail }: any) => {
    // Load
    void load(detail.message, detail.options);

    // Set the visibility to visible
    isVisible.value = true;
  });
  window.addEventListener(dialogEvents.OptionDialog.CLOSE, () => {
    // Set the visibility to hidden
    isVisible.value = false;
  });
});
</script>

<template>
  <!-- 👉 Confirm Dialog -->
  <VDialog max-width="500" :model-value="isVisible">
    <VCard :loading="loading" class="text-center px-10 py-6">
      <VCardText>
        <h6 class="text-lg font-weight-medium">
          {{ $t(state.message) }}
        </h6>
      </VCardText>

      <VCardText class="d-flex align-center justify-center gap-2">
        <template v-for="option in state.options">
          <VBtn
            :disabled="loading"
            :color="option.color ?? 'primary'"
            @click="handle(option.handler)"
          >
            {{ $t(option.label) }}
          </VBtn>
        </template>
      </VCardText>
    </VCard>
  </VDialog>
</template>
